// Maps a request to the zod schema of its 2xx body. Both halves are generated from the one
// committed document: the paths and operation ids come from it directly, and Orval names each
// operation's response schema `<PascalOperationId>Response` in the zod target. contract.test.ts
// fails if any operation is left without a schema.

import type { ZodType } from "zod";

import contract from "../../../../../openapi/arb-bot.openapi.json";
import * as schemas from "@/generated/zod/arb-bot.zod";

interface Route {
    method: string;
    path: string;
    pattern: RegExp;
    schema: ZodType | undefined;
}

const pascal = (operationId: string): string =>
    operationId
        .split(/[^A-Za-z0-9]+/)
        .filter(Boolean)
        .map((word) => word[0]!.toUpperCase() + word.slice(1))
        .join("");

/** A route template of the committed document, e.g. `/api/pools/{dex}`: a typo or a removed route is a type error. */
export type ContractPath = keyof typeof contract.paths;

// `/api/pools/{dex}` → /^\/api\/pools\/[^/]+$/
export const toPattern = (path: ContractPath | string): RegExp =>
    new RegExp(
        `^${path
            .split(/\{[^}]+\}/)
            .map((part) => part.replace(/[.*+?^$()|[\]\\]/g, "\\$&"))
            .join("[^/]+")}$`,
    );

const generated = schemas as Record<string, unknown>;

export const contractRoutes: readonly Route[] = Object.entries(contract.paths).flatMap(([path, item]) =>
    Object.entries(item as Record<string, { operationId: string }>).map(([method, operation]) => {
        const schema = generated[`${pascal(operation.operationId)}Response`];
        return {
            method: method.toUpperCase(),
            path,
            pattern: toPattern(path),
            schema: schema instanceof Object && "safeParse" in schema ? (schema as ZodType) : undefined,
        };
    }),
);

export function responseSchemaFor(method: string, path: string): ZodType | undefined {
    return contractRoutes.find((route) => route.method === method && route.pattern.test(path))?.schema;
}
